/**
 * Global Error Store for Talousakatemia
 * 
 * Manages error state for the ErrorBanner component.
 * Uses the standardized AppError shape with stable error codes.
 * Errors persist until explicitly cleared (no auto-dismiss).
 */

import { create } from 'zustand';
import type { AppError as CoreAppError, AppErrorCode, AppErrorSeverity, AppErrorRetry } from '@/core/errors/appError';

// Re-export types for backward compatibility
export type ErrorSeverity = AppErrorSeverity;

// Extended AppError with store-specific fields
export interface AppError extends Omit<CoreAppError, 'retry'> {
  id: string;
  timestamp: number;
  retryAction?: () => void;
}

interface ErrorState {
  errors: AppError[];
  
  // Add an error to the store (accepts AppError or partial)
  setError: (error: Omit<AppError, 'id' | 'timestamp'> | CoreAppError) => string;
  
  // Clear a specific error by ID
  clearError: (id: string) => void;
  
  // Clear all non-critical errors
  clearAllNonCritical: () => void;
  
  // Clear all errors (use with caution)
  clearAll: () => void;
  
  // Check if there are any errors
  hasErrors: () => boolean;
  
  // Check if there are critical errors
  hasCriticalErrors: () => boolean;
  
  // Get error by trace ID
  getErrorByTraceId: (traceId: string) => AppError | undefined;
}

// Generate unique error ID
let errorIdCounter = 0;
const generateErrorId = (): string => {
  errorIdCounter += 1;
  return `error-${Date.now()}-${errorIdCounter}`;
};

export const useErrorStore = create<ErrorState>((set, get) => ({
  errors: [],
  
  setError: (error) => {
    const id = generateErrorId();
    
    // Handle both CoreAppError and legacy format
    const retryAction = 'retry' in error && error.retry 
      ? error.retry.action as () => void
      : 'retryAction' in error 
        ? error.retryAction 
        : undefined;
    
    const newError: AppError = {
      id,
      code: 'code' in error ? error.code : 'FW_UNKNOWN' as AppErrorCode,
      title: error.title,
      message: error.message,
      severity: error.severity,
      traceId: error.traceId,
      context: error.context,
      cause: error.cause,
      retryAction,
      timestamp: Date.now(),
    };
    
    // Check for duplicate errors (same code + message within 5 seconds)
    const existingErrors = get().errors;
    const isDuplicate = existingErrors.some(
      e => e.code === newError.code && 
           e.message === newError.message && 
           Date.now() - e.timestamp < 5000
    );
    
    if (!isDuplicate) {
      set((state) => ({
        errors: [...state.errors, newError],
      }));
    }
    
    return id;
  },
  
  clearError: (id) => {
    set((state) => ({
      errors: state.errors.filter((e) => e.id !== id),
    }));
  },
  
  clearAllNonCritical: () => {
    set((state) => ({
      errors: state.errors.filter((e) => e.severity === 'critical'),
    }));
  },
  
  clearAll: () => {
    set({ errors: [] });
  },
  
  hasErrors: () => get().errors.length > 0,
  
  hasCriticalErrors: () => get().errors.some((e) => e.severity === 'critical'),
  
  getErrorByTraceId: (traceId: string) => {
    return get().errors.find((e) => e.traceId === traceId);
  },
}));

// Export singleton functions for use outside React
export const setGlobalError = (error: Omit<AppError, 'id' | 'timestamp'> | CoreAppError): string => {
  return useErrorStore.getState().setError(error);
};

export const clearGlobalError = (id: string): void => {
  useErrorStore.getState().clearError(id);
};

export const getErrorByTraceId = (traceId: string): AppError | undefined => {
  return useErrorStore.getState().getErrorByTraceId(traceId);
};
